import { events } from "@constants/tracking";
import useAnalytics from "@hooks/useAnalytics";
import { useAppTheme } from "@hooks/useAppTheme";
import { useToast } from "@memorang/ui/components/Toast";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { StyleSheet } from "react-native";
import { Button, Dialog, List, Portal, Text } from "react-native-paper";
import { useGlobalStore } from "../../../../hooks/useGlobalStore";

type Props = {
	visible: boolean;
	handleClose: () => void;
};

const LanguageDialog = ({ visible, handleClose }: Props) => {
	const { i18n, t } = useTranslation();

	const { explicitCallTrackCustomEvent } = useAnalytics();
	const setLanguage = useGlobalStore((state) => state.setLanguage);
	const { showToast } = useToast();
	const theme = useAppTheme();
	const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);

	const languages = [
		{ code: "en-US", label: "English", emoji: "🇺🇸" },
		{ code: "zh-CN", label: "中文", emoji: "🇨🇳" },
		{ code: "ja-JP", label: "日本語", emoji: "🇯🇵" },
	];

	const handleLanguageChange = (code: string) => {
		const prevLanguage =
			languages.find((lang) => lang.code === i18n.language)?.label ||
			i18n.language;
		const newLanguage =
			languages.find((lang) => lang.code === code)?.label || code;
		i18n.changeLanguage(code);
		setLanguage(code);
		handleClose();
		explicitCallTrackCustomEvent({
			eventName: events.userPreferenceChanged,
			newLanguage,
			currentLanguage: prevLanguage,
		});
		showToast({
			message: t("language.switchedMessage", {
				from: prevLanguage,
				to: newLanguage,
			}),
		});
	};

	const LanguageItem = ({ code, label, emoji }: (typeof languages)[0]) => (
		<List.Item
			onPress={() => setSelectedLanguage(code)}
			title={label}
			left={(props) => (
				<List.Icon {...props} icon={() => <Text>{emoji}</Text>} />
			)}
			right={(props) =>
				selectedLanguage === code ? (
					<List.Icon {...props} icon="check" color={theme.colors.primary} />
				) : null
			}
		/>
	);

	const getButtonLabel = () => {
		if (selectedLanguage === i18n.language) {
			return t("language.switchLanguage");
		}
		const newLanguage =
			languages.find((lang) => lang.code === selectedLanguage)?.label ||
			selectedLanguage;
		return t("language.switchTo", { language: newLanguage });
	};

	return (
		<Portal>
			<Dialog visible={visible} onDismiss={handleClose} style={styles.dialog}>
				<Dialog.Icon icon="translate" />
				<Dialog.Title
					style={{
						textAlign: "center",
					}}
				>
					{t("language.select")}
				</Dialog.Title>
				<Dialog.Content
					style={{
						paddingHorizontal: 0,
						gap: 16,
					}}
				>
					{languages.map((lang) => (
						<LanguageItem key={lang.code} {...lang} />
					))}
				</Dialog.Content>
				<Dialog.Actions>
					<Button onPress={handleClose}>{t("common.cancel")}</Button>
					<Button
						onPress={() => handleLanguageChange(selectedLanguage)}
						disabled={selectedLanguage === i18n.language}
					>
						{getButtonLabel()}
					</Button>
				</Dialog.Actions>
			</Dialog>
		</Portal>
	);
};

const styles = StyleSheet.create({
	dialog: {
		maxWidth: 400,
		width: "90%",
		alignSelf: "center",
	},
});

export default LanguageDialog;
